import { useLanguage } from '../contexts/LanguageContext';
import { ExternalLink, Globe, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const portfolioItems = [
  {
    company: 'YESFORT',
    name: 'App & Web Development',
    website: 'https://yesfort.com/de/home-de/',
    image: 'https://api.microlink.io/?url=https://yesfort.com/de/home-de/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'IT & Development',
  },
  {
    company: 'oFloor',
    name: 'Fugenlose Böden',
    website: 'https://www.o-floor.com/',
    image: 'https://api.microlink.io/?url=https://www.o-floor.com/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'Construction & Design',
  },
  {
    company: 'SAND & SILICON',
    name: 'Electronic Components',
    website: 'https://sandsilicon.com/',
    image: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/l7826ofz_image.png',
    industry: 'Electronics Distribution',
  },
  {
    company: 'Obrycki',
    name: 'Designerböden',
    website: 'https://obrycki.de/',
    image: 'https://api.microlink.io/?url=https://obrycki.de/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'Interior Design',
  },
  {
    company: 'SGF Rent Car',
    name: 'Car Rental Services',
    website: 'https://sgfrentcar.com/',
    image: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/0szatq0e_image.png',
    industry: 'Automotive',
  },
  {
    company: 'Eunde Personal',
    name: 'Personalvermittlung',
    website: 'https://eunde-personal.de/',
    image: 'https://api.microlink.io/?url=https://eunde-personal.de/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'HR & Recruitment',
  },
  {
    company: 'Abschleppdienst 24',
    name: 'Pannenhilfe & Abschleppen',
    website: 'https://www.abschleppdienst-24.com/',
    image: 'https://api.microlink.io/?url=https://www.abschleppdienst-24.com/&screenshot=true&meta=false&embed=screenshot.url',
    industry: 'Automotive Services',
  }
];

export const TestimonialsSection = () => {
  const { t, isRTL } = useLanguage();

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-slate-50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6">
            <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-medium text-sm sm:text-base">{t('testimonials.badge')}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            {t('testimonials.title')}
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {portfolioItems.slice(0, 6).map((item, index) => (
            <a
              key={index}
              href={item.website}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid={`portfolio-${index}`}
            >
              {/* Website Screenshot */}
              <div className="aspect-[16/10] overflow-hidden bg-slate-100 relative">
                <img
                  src={item.image}
                  alt={`${item.company} Website`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden absolute inset-0 bg-slate-200 items-center justify-center">
                  <Globe className="w-12 h-12 text-slate-400" />
                </div>
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-brand/0 group-hover:bg-brand/10 transition-colors duration-300" />
              </div>

              {/* Company Info */}
              <div className="p-5 sm:p-6">
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h3 className="font-bold text-slate-900 text-lg sm:text-xl group-hover:text-brand transition-colors">
                      {item.company}
                    </h3>
                    <p className="text-slate-500 text-sm">{item.industry}</p>
                  </div>
                  <div className="flex items-center gap-2 text-brand font-medium text-sm bg-brand/10 px-3 py-1.5 rounded-full group-hover:bg-brand group-hover:text-white transition-colors">
                    <ExternalLink className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('testimonials.visitSite')}</span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-10 sm:mt-12">
          <Link to="/portfolio">
            <Button
              variant="outline"
              className="h-12 sm:h-14 px-6 sm:px-8 rounded-full border-2 border-brand text-brand hover:bg-brand hover:text-white text-base sm:text-lg font-semibold transition-colors"
              data-testid="view-all-portfolio-btn"
            >
              {t('testimonials.viewAll')}
              <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
